/**
 * useProfileFormData Hook
 * 
 * Centralizes profile form data management with deterministic initialization
 * Handles different modes: newUser, userManagement, self, admin
 */

import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useLocation } from 'react-router-dom';
import {
  updateUserProfile,
  createAdditionalProfile
} from '../slices/userRoleSlice';
import useRoleNormalization from './useRoleNormalization';

/**
 * Hook for managing profile form data with deterministic initialization
 * @param {Object} params - Configuration object
 * @param {string} params.profileType - Type of profile (admin, bookCreator, voiceActor)
 * @param {string} params.userId - Target user ID being edited
 * @param {Object} params.profile - Profile object from API/Redux
 * @param {Object} params.initialUserData - Data from Add New User form
 * @param {string} params.mode - Operation mode (auto, newUser, userManagement, self, admin)
 * @param {Function} params.onSave - Optional callback after successful save
 * @returns {Object} - Form state and handlers
 */
const useProfileFormData = ({
  profileType,
  userId,
  profile = null,
  initialUserData = null,
  mode = 'auto',
  onSave = null
}) => {
  const dispatch = useDispatch();
  const location = useLocation();

  // Get utilities from useRoleNormalization
  const {
    normalizeRole,
    getProfileDefaults,
    getEmptyProfileTemplate,
    extractBasicUserData,
    mergeProfileData,
    extractRoleFromProfileType
  } = useRoleNormalization();

  // State management
  const [formState, setFormState] = useState('initializing');
  const [isDirty, setIsDirty] = useState(false);
  const [error, setError] = useState(null);
  const [saveStatus, setSaveStatus] = useState(null);

  // Redux selectors
  const currentUser = useSelector(state => state.user.profile?.data);
  const currentUserId = currentUser?.id || currentUser?.userId;
  const isAdmin = currentUser?.primaryRole === 'admin';

  // Normalize the profile type
  const normalizedProfileType = normalizeRole(profileType);

  // Determine operation mode
  const operationMode = useMemo(() => {
    if (mode !== 'auto') return mode;

    // Auto-detect mode based on context
    if (initialUserData && (!profile || !profile.uuid)) {
      console.log('useProfileFormData: Detected newUser mode');
      return 'newUser';
    }

    if (location.pathname.includes('/admin/user-profile/')) {
      console.log('useProfileFormData: Detected userManagement mode');
      return 'userManagement';
    }

    if (currentUserId === userId) {
      console.log('useProfileFormData: Detected self mode');
      return 'self';
    }

    if (isAdmin && userId !== currentUserId) {
      console.log('useProfileFormData: Detected admin mode');
      return 'admin';
    }

    console.log('useProfileFormData: Using default self mode');
    return 'self';
  }, [mode, initialUserData, profile, location.pathname, currentUserId, userId, isAdmin]);

  // Determine target user ID
  const targetUserId = useMemo(() => {
    // For new users, use the provided userId
    if (operationMode === 'newUser') {
      return userId || currentUserId;
    }

    // For user management, always use the provided userId
    if (operationMode === 'userManagement' || operationMode === 'admin') {
      if (!userId) {
        console.error('useProfileFormData: userId is required for userManagement/admin mode');
      }
      return userId;
    }

    // For self-edit, use current user ID
    if (operationMode === 'self') {
      return currentUserId;
    }

    return userId || currentUserId;
  }, [operationMode, userId, currentUserId]);

  // Build initial values based on mode and data sources
  const initialValues = useMemo(() => {
    console.log('useProfileFormData: Building initial values', {
      operationMode,
      hasInitialUserData: !!initialUserData,
      hasProfile: !!profile,
      profileType: normalizedProfileType
    });

    const sources = [];

    // Priority 1: New user data (highest priority)
    if (operationMode === 'newUser' && initialUserData) {
      console.log('useProfileFormData: Using initialUserData as primary source');
      sources.push(extractBasicUserData(initialUserData));
    }

    // Priority 2: Existing profile data
    if (profile && profile.uuid) {
      console.log('useProfileFormData: Using existing profile data');
      console.log('useProfileFormData: Profile object:', profile);
      console.log('useProfileFormData: Profile firstName:', profile.firstName);
      console.log('useProfileFormData: Profile lastName:', profile.lastName);

      // If profile doesn't have firstName/lastName but we have initialUserData with them, merge them
      if ((!profile.firstName || !profile.lastName) && initialUserData && (initialUserData.firstName || initialUserData.lastName)) {
        console.log('useProfileFormData: Profile missing name fields, merging with initialUserData');
        sources.push({
          ...profile,
          firstName: profile.firstName || initialUserData.firstName || '',
          lastName: profile.lastName || initialUserData.lastName || ''
        });
      } else {
        sources.push(profile);
      }
    }

    // Priority 3: Copy from primary profile (for secondary profiles)
    if (operationMode === 'newUser' && !profile && initialUserData) {
      // For new users, all profiles should get the basic user data
      console.log('useProfileFormData: Copying basic data for secondary profile');
      sources.push(extractBasicUserData(initialUserData));
    }

    // Priority 3.5: Use initialUserData as fallback for missing name fields
    if (!profile?.firstName && !profile?.lastName && initialUserData && (initialUserData.firstName || initialUserData.lastName)) {
      console.log('useProfileFormData: Adding initialUserData for name fields');
      sources.push({
        firstName: initialUserData.firstName || '',
        lastName: initialUserData.lastName || ''
      });
    }

    // Priority 4: Profile type defaults
    console.log('useProfileFormData: Adding profile type defaults');
    sources.push(getProfileDefaults(normalizedProfileType));

    // Priority 5: Empty template (lowest priority)
    console.log('useProfileFormData: Adding empty template as fallback');
    sources.push(getEmptyProfileTemplate(normalizedProfileType));

    // Merge all sources with proper priority
    const merged = mergeProfileData(sources);

    console.log('useProfileFormData: Final initial values:', merged);
    return merged;
  }, [
    operationMode,
    initialUserData,
    profile,
    normalizedProfileType,
    extractBasicUserData,
    getProfileDefaults,
    getEmptyProfileTemplate,
    mergeProfileData
  ]);

  // Update form state when data changes
  useEffect(() => {
    if (operationMode && targetUserId && initialValues) {
      console.log('useProfileFormData: Setting form state to ready');
      setFormState('ready');
    }
  }, [operationMode, targetUserId, initialValues]);

  // Store profile in a ref to ensure we always have the latest value
  const profileRef = useRef(profile);
  useEffect(() => {
    profileRef.current = profile;
  }, [profile]);

  // Handle save
  const handleSave = useCallback(async (values) => {
    const currentProfile = profileRef.current;

    console.log('🔵 useProfileFormData: Starting save process', {
      profileType: normalizedProfileType,
      targetUserId,
      operationMode,
      hasProfileUuid: !!currentProfile?.uuid,
      profileUuid: currentProfile?.uuid
    });

    console.log('📊 useProfileFormData: Values received for save:', values);
    console.log('📊 useProfileFormData: Current profile data:', currentProfile);

    setFormState('updating');
    setError(null);
    setSaveStatus(null);

    try {
      let result;

      // If profile doesn't exist, create it
      if (!currentProfile || !currentProfile.uuid) {
        console.log('🆕 useProfileFormData: Creating new profile');
        const createPayload = {
          userId: targetUserId,
          profileType: normalizedProfileType,
          profileData: values
        };
        console.log('📤 useProfileFormData: Create payload:', createPayload);

        result = await dispatch(createAdditionalProfile(createPayload)).unwrap();
        setSaveStatus('created');
      } else {
        // Update existing profile
        console.log('🔄 useProfileFormData: Updating existing profile', {
          profileUuid: currentProfile.uuid,
          profileType: currentProfile.profileType,
          userId: targetUserId
        });

        if (!currentProfile.uuid) {
          throw new Error('Profile UUID is missing');
        }

        // Filter out internal fields (starting with _)
        const cleanedValues = Object.keys(values)
          .filter(key => !key.startsWith('_'))
          .reduce((obj, key) => {
            obj[key] = values[key];
            return obj;
          }, {});

        // Include profileType in the payload for the API endpoint
        const updatePayload = {
          userId: targetUserId,
          profileUuid: currentProfile.uuid,
          profileType: normalizedProfileType,  // Add profileType for API endpoint
          profileData: cleanedValues
        };

        console.log('📤 useProfileFormData: Update payload being sent to Redux:', updatePayload);
        console.log('📤 useProfileFormData: profileData fields:', Object.keys(updatePayload.profileData));
        console.log('📤 useProfileFormData: profileType:', normalizedProfileType);

        result = await dispatch(updateUserProfile(updatePayload)).unwrap();
        setSaveStatus('updated');
      }

      setFormState('ready');
      setIsDirty(false);

      // Call optional callback
      if (onSave) {
        onSave(result);
      }

      return result;
    } catch (err) {
      console.error('useProfileFormData: Save failed', err);
      setError(err.message || err.error || 'Failed to save profile');
      // Don't reset isDirty on error - let user retry
      // setIsDirty(false); // DON'T do this
      setFormState('ready'); // Set back to ready so user can retry
      setSaveStatus('error');
      throw err;
    }
  }, [
    dispatch,
    targetUserId,
    normalizedProfileType,
    operationMode,
    onSave
    // Note: profileRef is accessed via .current, so no need in deps
  ]);

  // Handle reset
  const handleReset = useCallback(() => {
    console.log('useProfileFormData: Resetting form');
    setIsDirty(false);
    setError(null);
    setSaveStatus(null);
    setFormState('ready');
  }, []);

  // Handle field change (to track dirty state)
  const handleFieldChange = useCallback(() => {
    console.log('🔧 handleFieldChange called, current isDirty:', isDirty);
    if (!isDirty) {
      console.log('🔧 Setting isDirty to true');
      setIsDirty(true);
    } else {
      console.log('🔧 isDirty already true, skipping setState');
    }
  }, [isDirty]);

  // Determine if form is editable
  const isEditable = useMemo(() => {
    // Always editable in self mode
    if (operationMode === 'self') return true;

    // Editable for admins in admin/userManagement mode
    if ((operationMode === 'admin' || operationMode === 'userManagement') && isAdmin) {
      return true;
    }

    // Editable for new users
    if (operationMode === 'newUser') return true;

    return false;
  }, [operationMode, isAdmin]);

  // Build user info for display
  const userInfo = useMemo(() => {
    if (profile) {
      return {
        name: `${profile.firstName || ''} ${profile.lastName || ''}`.trim() || 'Unknown',
        email: profile.email || '',
        userId: targetUserId
      };
    }

    if (initialUserData) {
      return {
        name: `${initialUserData.firstName || ''} ${initialUserData.lastName || ''}`.trim() || 'Unknown',
        email: initialUserData.email || '',
        userId: targetUserId
      };
    }

    return {
      name: 'Unknown User',
      email: '',
      userId: targetUserId
    };
  }, [profile, initialUserData, targetUserId]);

  return {
    // Form state
    formState,
    initialValues,
    isDirty,
    error,
    saveStatus,

    // Identifiers
    targetUserId,
    operationMode,
    profileType: normalizedProfileType,

    // User info
    userInfo,

    // Permissions
    isEditable,

    // Handlers
    handleSave,
    handleReset,
    handleFieldChange,

    // Profile data
    profileData: profile,
    initialUserData
  };
};

export default useProfileFormData;
